import { purchaseConversion, type GoogleAdsEnvironment } from './googleAds';
import type { VerifiedPaymentStatus } from './paymentState';
import { cartItemAmountGrosze } from './cartMoney';

export interface AnalyticsItem { item_id: string; price: number; quantity: number }
export interface AnalyticsQuote { items: AnalyticsItem[]; value: number; shipping: number; total: number }
export const ANALYTICS_PURCHASE_STORAGE = 'bukietowa.analytics-purchases.v1';
export const ANALYTICS_QUOTE_PREFIX = 'bukietowa.analytics-quote.v1:';
const RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
const SAFE_ID = /^(?:gid:\/\/shopify\/ProductVariant\/\d+|[A-Za-z0-9_-]{1,100})$/;

/** Only catalog identities and amounts. Never copy cart attributes or delivery details. */
export function analyticsItems(lines: ReadonlyArray<{ variantId?: string | null; addonId?: string; price?: string | number; quantity: number; unitAmountGrosze?: number }>): AnalyticsItem[] {
  return lines.flatMap(line => {
    const id = line.variantId ?? line.addonId;
    const amount = cartItemAmountGrosze(line);
    if (amount === null) return [];
    const price = amount / 100;
    if (typeof id !== 'string' || !id || !SAFE_ID.test(id) || !Number.isFinite(price) || price < 0 || !Number.isSafeInteger(line.quantity) || line.quantity <= 0) return [];
    return [{ item_id: id, price: Math.round(price * 100) / 100, quantity: line.quantity }];
  });
}
export function analyticsQuote(quote: { lines: ReadonlyArray<{ variantId?: string | null; quantity: number; unitAmountGrosze: number }>; itemsAmountGrosze: number; deliveryAmountGrosze: number; amountGrosze: number }): AnalyticsQuote | null {
  // Attribute-priced extras have no Shopify variant; group them without their text.
  const items = analyticsItems(quote.lines.map(line => ({ ...line, variantId: line.variantId ?? 'attribute-addon' })));
  if (!items.length || items.length !== quote.lines.length || ![quote.itemsAmountGrosze, quote.deliveryAmountGrosze, quote.amountGrosze].every(n => Number.isSafeInteger(n) && n >= 0)) return null;
  if (items.reduce((n, item) => n + Math.round(item.price * 100) * item.quantity, 0) !== quote.itemsAmountGrosze || quote.itemsAmountGrosze + quote.deliveryAmountGrosze !== quote.amountGrosze) return null;
  return { items, value: quote.itemsAmountGrosze / 100, shipping: quote.deliveryAmountGrosze / 100, total: quote.amountGrosze / 100 };
}
export function analyticsPageLocation(href: string): string {
  try {
    const url = new URL(href);
    const clean = new URL(url.origin + url.pathname);
    for (const key of ['gclid', 'gbraid', 'wbraid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_content', 'utm_term']) {
      const value = url.searchParams.get(key);
      if (value && /^[A-Za-z0-9_.+ -]{1,200}$/.test(value)) clean.searchParams.set(key, value);
    }
    return clean.href;
  } catch { return ''; }
}
function safeReferrer(href: string): string {
  try { return new URL(href).origin + '/'; } catch { return ''; }
}

/** Consent transport is owned by browserGoogleMeasurement, shared with Ads. */
export function createGoogleAnalytics(config: { tagId: string; hosts: readonly string[] }, env: GoogleAdsEnvironment) {
  let allowed = false, ready = false, configured = false;
  let loading: Promise<boolean> | null = null;
  let previousPage = '';
  const pending = new Set<string>();
  const sent = new Set<string>();
  const permitted = () => {
    try { return allowed && /^G-[A-Z0-9]+$/.test(config.tagId) && config.hosts.includes(new URL(env.location()).hostname); } catch { return false; }
  };
  const pageFields = () => ({ page_location: analyticsPageLocation(env.location()), page_referrer: previousPage || safeReferrer(env.referrer()) });
  function saved(): Record<string, number> {
    try {
      const value = JSON.parse(env.storage.getItem(ANALYTICS_PURCHASE_STORAGE) ?? '{}');
      if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
      return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, number] => entry[0].startsWith(config.tagId + ':') && typeof entry[1] === 'number' && entry[1] > env.now() - RETENTION_MS && entry[1] <= env.now()));
    } catch { return {}; }
  }
  async function updateConsent(analytics: boolean) {
    allowed = analytics;
    if (!allowed) {
      previousPage = '';
      // Erase optional storage/cookies, but retain queued and completed IDs in
      // this page's memory so a later consent choice cannot resend a receipt.
      try { env.storage.removeItem(ANALYTICS_PURCHASE_STORAGE); } catch { /* Optional storage. */ }
      env.clearAttribution();
      return false;
    }
    if (!permitted()) return false;
    try { if (env.storage.getItem(ANALYTICS_PURCHASE_STORAGE)) env.storage.setItem(ANALYTICS_PURCHASE_STORAGE, JSON.stringify(saved())); } catch { /* Optional storage. */ }
    if (!configured) {
      configured = true;
      env.command('config', config.tagId, { ...pageFields(), send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false, cookie_expires: 90 * 24 * 60 * 60 });
    }
    if (!ready && !loading) loading = env.loadTag(config.tagId).then(value => { ready = value; return value; }).catch(() => false).finally(() => { loading = null; });
    if (loading) await loading;
    return ready && permitted();
  }
  function pageView() {
    if (!ready || !permitted()) return false;
    const location = analyticsPageLocation(env.location());
    if (previousPage === location) return false;
    env.command('event', 'page_view', { send_to: config.tagId, ...pageFields() });
    previousPage = location;
    return true;
  }
  function ecommerce(name: 'view_item' | 'add_to_cart' | 'begin_checkout', items: AnalyticsItem[]) {
    if (!ready || !permitted() || !items.length) return false;
    // Rebuild even typed input: no accidental extra fields reach Google.
    const safeItems = analyticsItems(items.map(item => ({ variantId: item.item_id, price: item.price, quantity: item.quantity })));
    if (safeItems.length !== items.length) return false;
    env.command('event', name, { send_to: config.tagId, ...pageFields(), currency: 'PLN', value: safeItems.reduce((sum, item) => sum + Math.round(item.price * 100) * item.quantity, 0) / 100, items: safeItems });
    return true;
  }
  function trackPurchase(id: string | null, status: VerifiedPaymentStatus | null, quote: AnalyticsQuote | null) {
    const purchase = purchaseConversion(id, status);
    // New receipts carry their verified catalog snapshot. Session storage is
    // only a compatibility fallback for older receipts, not a prerequisite
    // for measuring a paid order after a late consent choice or lost quote.
    if (status?.measurement !== undefined) quote = status.measurement;
    if (!purchase || !quote || !Array.isArray(quote.items) || !quote.items.every(item => item && typeof item.item_id === 'string' && typeof item.price === 'number' && Number.isFinite(item.price) && Number.isSafeInteger(item.quantity) && item.quantity > 0) || ![quote.value, quote.shipping, quote.total].every(Number.isFinite) || !ready || !permitted()) return false;
    const validated = analyticsQuote({ lines: quote.items.map(item => ({ variantId: item.item_id, quantity: item.quantity, unitAmountGrosze: Math.round(item.price * 100) })), itemsAmountGrosze: Math.round(quote.value * 100), deliveryAmountGrosze: Math.round(quote.shipping * 100), amountGrosze: Math.round(quote.total * 100) });
    if (!validated || Math.round(validated.total * 100) !== status!.amountGrosze) return false;
    const key = `${config.tagId}:${purchase.transaction_id}`;
    if (pending.has(key) || sent.has(key)) return false;
    if (saved()[key]) { sent.add(key); return false; }
    pending.add(key);
    env.command('event', 'purchase', { send_to: config.tagId, ...pageFields(), transaction_id: purchase.transaction_id, currency: 'PLN', value: validated.value, shipping: validated.shipping, items: validated.items,
      event_callback: () => {
        pending.delete(key);
        sent.add(key);
        if (!permitted()) return;
        try { env.storage.setItem(ANALYTICS_PURCHASE_STORAGE, JSON.stringify({ ...saved(), [key]: env.now() })); } catch { /* Google transaction_id also deduplicates. */ }
      },
    });
    return true;
  }
  return { updateConsent, pageView, ecommerce, trackPurchase };
}
